import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// A MEMBER CANCELS THEIR OWN BOOKING (#1135)
//
// `POST /member/bookings/:id/cancel` (a court, by its `book_…` id) or
// `POST /member/asset-bookings/:id/cancel` (a machine booked on its own). The
// request carries nothing but the id: the server decides who the member is, the
// deadline, and the refund — the whole captured payment, owner decision
// 2026-11-01. The answer is a CODE; the screen decides the sentence.
// ─────────────────────────────────────────────────────────────────────

export type CancelSubject = 'booking' | 'asset_booking';

export type CancelOwnBookingResult =
  /** Cancelled. `refundMinor` is what goes back to the member (0 when free). */
  | { readonly state: 'cancelled'; readonly refundMinor: number }
  /**
   * The provider has not confirmed the refund. The court is STILL HELD and the
   * club's reconciliation settles it — the screen must not say «avbestilt».
   */
  | { readonly state: 'unconfirmed' }
  /** Refused, with the server's code (`too_late`, `refund_failed`, …). */
  | { readonly state: 'refused'; readonly code: string }
  /** The request never got an answer. Nothing is known; nothing changed here. */
  | { readonly state: 'error'; readonly status: number };

export async function cancelOwnBooking(
  id: string,
  subject: CancelSubject = 'booking',
): Promise<CancelOwnBookingResult> {
  const path =
    subject === 'booking'
      ? `/member/bookings/${encodeURIComponent(id)}/cancel`
      : `/member/asset-bookings/${encodeURIComponent(id)}/cancel`;
  let res: Response;
  try {
    res = await apiFetch(path, { method: 'POST' });
  } catch {
    return { state: 'error', status: 0 };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.status === 200 && body?.cancelled === true) {
    return {
      state: 'cancelled',
      refundMinor: typeof body.refundMinor === 'number' ? body.refundMinor : 0,
    };
  }
  if (res.status === 202) return { state: 'unconfirmed' };
  if (typeof body?.error === 'string') return { state: 'refused', code: body.error };
  return { state: 'error', status: res.status };
}
